import React from 'react';
import { Flex, PopoverV2, useDisclosure } from '@withjoy/joykit';

import { ListCheckboxRow } from './ListCheckboxRow';
import { useActivePrintFiltersVar } from '../../../utils/activePrintFiltersCache';
import type { FilterFacet } from '../Filter.types';
import { LearnMoreLink } from '../../LearnMoreLink';
import { FormatFilterPill } from './FormatFilterPill';
import { useDesignGalleryFiltersContext } from '../../../DesignGalleryFiltersProvider';
import { PopoverBodyOverride, PillTrigger as Trigger } from './FilterShared';

export { PopoverBodyOverride, PillTrigger as Trigger } from './FilterShared';

interface FilterPillProps {
  facet: FilterFacet;
  onFilterChange: React.ChangeEventHandler<HTMLInputElement>;
  interaction: 'popover' | 'drawer';
  onTriggerFilterDrawer: (groupCode: string) => void;
}

export const FilterPill = React.memo((props: FilterPillProps) => {
  const { facet, interaction, onFilterChange, onTriggerFilterDrawer } = props;
  const { label, options } = facet;
  const { isOpen, onClose, onOpen } = useDisclosure();

  useActivePrintFiltersVar(); // To rerender component when activeFiltersCount changes

  const { getActiveFilterCountByGroupCode } = useDesignGalleryFiltersContext();
  const activeFiltersCount = getActiveFilterCountByGroupCode(facet.groupCode);

  // The "format" facet is an oddball. It is cross-category and is stored in a separate query param.
  if (facet.groupCode === 'format') {
    return <FormatFilterPill />;
  }

  if (interaction === 'drawer') {
    return (
      <Trigger
        isOpen={false}
        activeFiltersCount={activeFiltersCount}
        label={label}
        onClick={() => onTriggerFilterDrawer(facet.groupCode)}
      />
    );
  }

  return (
    <PopoverV2
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottomStart"
      overrides={{
        Body: {
          props: {
            maxHeight: 320,
            maxWidth: 320,
            overflowY: 'scroll',
          },
          component: PopoverBodyOverride,
        },
      }}
    >
      <PopoverV2.Trigger>
        <Trigger
          isOpen={isOpen}
          activeFiltersCount={activeFiltersCount}
          label={label}
        />
      </PopoverV2.Trigger>
      <PopoverV2.Content>
        <Flex
          flexDirection="column"
          paddingX={5}
          paddingY={4}
          rowGap={2}
        >
          {options.map(option => (
            <ListCheckboxRow
              key={option.compositeCode}
              {...option}
              onChange={onFilterChange}
            />
          ))}
          {facet.learnMoreUrl && (
            <LearnMoreLink
              url={facet.learnMoreUrl}
              marginTop={4}
            />
          )}
        </Flex>
      </PopoverV2.Content>
    </PopoverV2>
  );
});
